/** Who is in this workspace, and what they may do — **S11**.
 *
 * There was no way to see a colleague, change a role or suspend anybody. The first Admin existed
 * because the seeder made them, and a customer whose finance lead joined could do nothing about
 * it. Auth, roles, memberships and the audit log all existed; only this did not.
 *
 * **Three things the screen has to get right:**
 *
 * - **A role is a capability, so it says what it lets somebody do.** "Approver" tells a reader
 *   nothing; "accepts figures into the inventory and issues filings" tells them whether to grant
 *   it. This is the screen where a mis-set role becomes somebody approving their own work.
 * - **The refusals are explained before they are hit.** The last Admin's controls are disabled
 *   with the reason on the row, rather than offering a change that 409s — a button that
 *   predictably fails is worse than one that is not there.
 * - **Inviting hands over a token, and may also ask for an email** (ADR-0046). The screen shows the
 *   token once and says where the person enters it (`/join`, with the address), so the token works
 *   whether or not any mail arrives. Issuing and re-sending both ask the email port for a message
 *   (`core/email.py`); the answer is `email_requested`, which means mail was *asked for* and never
 *   that it was delivered, so the screen says "requested" and "delivery is not confirmed" and must
 *   never read as "sent". `false` (no sender configured, or the address could not be rendered)
 *   says no email was requested and the token has to be handed over. An estate whose API predates
 *   the field omits it, which reads as `false`.
 * - **An invitation cannot change a member**, so typing an existing member's address shows why and
 *   disables the control, rather than issuing a token that redeems to nothing.
 * - **An Auditor's invitation carries its engagement** (ADR-0048). The form asks for the data an
 *   auditor may read, the day their access ends and **the filings they are engaged for**, and
 *   *only* for an Auditor: a control a role cannot use is not rendered
 *   (`30-design-principles.md` rule 6), and the server refuses a period on any other role. Before
 *   this the only path that creates a member could not set either, so an invited Auditor read every
 *   year for ever — and, once it could, still read every *company*: a window says when and nothing
 *   said whose. The filings are the answer to that, and the companies an auditor may read follow
 *   from them, so there is no list of companies to choose.
 * - **An existing member's period can be changed** (gate 14): an Auditor's data window and last day
 *   of access, and any other role's optional last day. The control is `PeopleEngagement.tsx`; it is
 *   what the "suspend them and invite again" advice on an unscoped Auditor used to stand in for.
 * - **A role change cannot make an Auditor.** The server refuses it (`auditor_needs_an_engagement`)
 *   because an engagement is a window, an end and a filing, and a dropdown carries none of them; so
 *   the dropdown does not offer it, rather than offering a change that predictably 409s.
 */

import { UsersThree } from "@phosphor-icons/react";
import { Link } from "@tanstack/react-router";
import { useState } from "react";
import type { FormEvent } from "react";

import { FieldError } from "../components/FieldError";
import { Loaded, ReadFailed, WriteFailed } from "../components/Loaded";
import { useFocusRefusal } from "../components/useFocusRefusal";
import { useFormChecks } from "../components/useFormChecks";
import { Panel } from "../components/Panel";
import { mayHaveQueue, maySubmit } from "../api/approvals";
import { useMe } from "../api/auth";
import {
  ALREADY_A_MEMBER,
  RESEND_ADDRESS_MISMATCH,
  refusedField,
  useInvitations,
  useInviteMember,
  useResendInvitation,
  useRevokeInvitation,
} from "../api/invitations";
import type { InvitationRequest, OutstandingInvitation } from "../api/invitations";
import { useMembers, useObligations, useUpdateMember } from "../api/queries";
import type { Member, MemberRole } from "../api/queries";
import { shortDate } from "../lib/format";
import { Refused } from "../api/refusal";
import {
  Drawer,
  EmptyState,
  EMPTY_FILTERS,
  FilterLine,
  GridTable,
  InfoPopover,
  StatusChip,
  TablePage,
  Tile,
  TileStrip,
} from "../components/ui";
import type { FilterState, GridColumn } from "../components/ui";
import { RequireSession } from "../shell/RequireSession";
import { NAV_SECTIONS, mayEnterData } from "../shell/routeTable";
import { useGridSort } from "./gridSort";
import { EngagementPeriod } from "./PeopleEngagement";
import { CAN, LABEL, ROLES, filingLabel, periodOf } from "./peopleRoles";
import { EngagementReach } from "./PeopleReach";
import { ScreenFrame } from "./ScreenFrame";
import { keepSearch } from "./search";
import styles from "./Screen.module.css";
import own from "./People.module.css";
import { useScreen } from "./useScreen";

function cls(...names: Array<string | false | undefined>): string {
  return names.filter(Boolean).join(" ");
}

/** What an outstanding invitation will become, in the Admin's words. */
function engagementLine(invitation: OutstandingInvitation): string | null {
  const clauses: string[] = [];
  if (invitation.scope_from && invitation.scope_to) {
    clauses.push(`data from ${shortDate(invitation.scope_from)} to ${shortDate(invitation.scope_to)}`);
  }
  if (invitation.valid_to) clauses.push(`access ends ${shortDate(invitation.valid_to)}`);
  // **Whose**, by the company's name: an Admin looking at an outstanding Auditor invitation has to
  // be able to see that it is for the company they meant before it is accepted, and a UUID is not
  // a way to do that.
  const companies = [...new Set((invitation.engagement ?? []).map((f) => f.legal_entity_name))];
  if (companies.length) clauses.push(`for ${companies.join(", ")}`);
  return clauses.length ? clauses.join(" · ") : null;
}


/** Where a person enters an invitation token: the Join screen on this same origin.
 *
 * Shown beside a freshly issued token because the token alone is a dead end — an Admin handing over
 * a string with no address to put it at has told the person nothing they can act on. **Never
 * carries the token**: ADR-0046 keeps it out of every address, so the person types it. */
function joinLocation(): string {
  return `${window.location.origin}/join`;
}

/** Whether the address an Admin is typing already belongs to somebody in this workspace.
 *
 * An invitation cannot change an existing member: redeeming one for an address that already has a
 * membership here attaches nothing, and the role the invitation named is silently not applied (and
 * for a suspended member the redemption is refused in the one uniform voice). So an Admin who
 * "invites" a Viewer as an Approver is told the person joined and finds they are still a Viewer. The
 * roster is the Admin's own and already prints every address, so naming a match discloses nothing
 * they cannot see two panels up. Compared the way the server compares: trimmed and lowercased. */
function existingMember(roster: readonly Member[] | undefined, typed: string): Member | null {
  const wanted = typed.trim().toLowerCase();
  if (wanted === "" || !roster) return null;
  return roster.find((m) => m.email.trim().toLowerCase() === wanted) ?? null;
}

/** Invite somebody. **The token is shown once, here, and held in this component's state only.**
 *
 * The engagement fields are rendered for an Auditor and for nobody else. That is not tidiness: the
 * server refuses a data window on any other role (`engagement_period_not_applicable`), so showing
 * the fields for an Approver would be a control that predictably fails — design rule 6 — and the
 * state is cleared on a role change so a window typed for an Auditor is never sent for someone else.
 */
export function InvitePanel({ initialRole = "viewer" }: { initialRole?: MemberRole } = {}) {
  const invite = useInviteMember();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState<MemberRole>(initialRole);
  const [scopeFrom, setScopeFrom] = useState("");
  const [scopeTo, setScopeTo] = useState("");
  const [validTo, setValidTo] = useState("");
  //: The filings an Auditor is engaged for, by obligation id. Cleared when the role stops being
  //: Auditor, for the reason the window is: a filing ticked for an Auditor must never travel with
  //: somebody else's invitation.
  const [filings, setFilings] = useState<string[]>([]);
  const obligations = useObligations();
  //: The same query the Members panel reads (`["members"]`), so this adds no request. A failed or
  //: pending read means *no warning*, never a block: the server decides what is allowed.
  const roster = useMembers(true);
  const [refusal, setRefusal] = useState<unknown>(null);
  //: The credential. Component state and nowhere else: not the query cache (`gcTime: 0`), not the
  //: URL, not storage. "I have passed it on" is what drops it.
  const [issued, setIssued] = useState<{
    token: string;
    expiresAt: string;
    role: MemberRole;
    address: string;
    emailRequested: boolean;
  } | null>(null);

  const auditor = role === "auditor";
  const refused = refusedField(refusal);
  const already = existingMember(roster.data, email);
  const [checks, attach] = useFormChecks();
  //: After the server refuses, focus goes to the field it named (or its sentence).
  useFocusRefusal(checks.element, refusal);

  async function submit(event: FormEvent) {
    event.preventDefault();
    setRefusal(null);
    //: The form's own check mirrors the server's so it does not send a request that is certain to be
    //: refused. The server remains the authority on every one of these. **The button is never greyed
    //: for them** (Q-06, G-Wadmin-02): an Auditor invitation waited silently on a third date, and
    //: nothing named it. Pressing now says which box, in the order they are on the screen.
    const ok = checks.run([
      { field: "email", ok: email.trim() !== "", message: "Type the email address to invite." },
      { field: "email", ok: !email.trim() || email.includes("@"), message: "That does not look like an email address." },
      {
        field: "email",
        ok: !already,
        message: "That address already belongs to a member here: see the note below.",
      },
      ...(auditor
        ? [
            { field: "from", ok: scopeFrom !== "", message: "Choose the first day of the data they may read." },
            { field: "to", ok: scopeTo !== "", message: "Choose the last day of the data they may read." },
            {
              field: "to",
              ok: scopeFrom === "" || scopeTo === "" || scopeFrom <= scopeTo,
              message: "The data window cannot end before it starts.",
            },
            //: At least one filing. **Whose** data is not optional: the server refuses an Auditor
            //: invitation naming none (`engagement_obligations_required`).
            { field: "filings", ok: filings.length > 0, message: "Tick at least one filing they are engaged for." },
            { field: "end", ok: validTo !== "", message: "Choose the last day they may sign in. An engagement is for a time." },
          ]
        : []),
    ]);
    if (!ok || invite.isPending) return;
    const request: InvitationRequest = {
      email: email.trim(),
      role,
      ...(auditor ? { scopeFrom, scopeTo, obligationIds: filings } : {}),
      ...(validTo ? { validTo } : {}),
    };
    try {
      const result = await invite.mutateAsync(request);
      setIssued({
        token: result.token,
        expiresAt: result.expires_at,
        role,
        address: request.email,
        // `=== true`: a response without the field (an API that predates it) is not a request.
        emailRequested: (result as { email_requested?: boolean }).email_requested === true,
      });
      setEmail("");
      setScopeFrom("");
      setScopeTo("");
      setValidTo("");
      setFilings([]);
    } catch (failure) {
      setRefusal(failure);
    }
  }

  return (
    <Panel title="Invite someone" sub="a single-use token, with an email requested where one is set up" column="1 / 9" row="1">
      {issued ? (
        <div className={cls(own.issued)} data-testid="invite-issued">
          <strong>
            Invitation for a{issued.role === "admin" || issued.role === "auditor" ? "n" : ""}{" "}
            {LABEL[issued.role] ?? issued.role}
          </strong>
          <code className={cls(own.token)} data-testid="invite-token">
            {issued.token}
          </code>
          <span data-testid="invite-email-note" data-email-requested={issued.emailRequested}>
            Shown once. It cannot be fetched again. It works only with the address you invited, and
            expires on {shortDate(issued.expiresAt)}.{" "}
            {issued.emailRequested
              ? `An email was requested to ${issued.address}, but delivery is not confirmed. The token above still works: if the email does not arrive, give it to them yourself.`
              : "No email was requested, so nothing has been sent to anyone: give the token to them yourself."}
          </span>
          <span data-testid="invite-join-location">
            Tell them to open <code>{joinLocation()}</code> and enter the token with that email
            address. Do not put the token in a link.
          </span>
          <button
            type="button"
            className={styles.secondary}
            data-testid="invite-done"
            onClick={() => setIssued(null)}
          >
            I have passed it on
          </button>
        </div>
      ) : null}

      <form
        ref={attach}
        className={cls(own.form)}
        onSubmit={(event) => void submit(event)}
        noValidate
        data-testid="invite-form"
      >
        <span className={cls(own.fieldRow)}>
          <label className={cls(own.field)}>
            Email address
            <input
              type="email"
              value={email}
              required
              autoComplete="off"
              {...checks.field("email")}
              data-testid="invite-email"
              onChange={(event) => {
                checks.touch("email");
                setEmail(event.target.value);
              }}
            />
            <FieldError {...checks.say("email")} />
          </label>
          <label className={cls(own.field)}>
            Role
            <select
              value={role}
              data-testid="invite-role"
              onChange={(event) => {
                const next = event.target.value as MemberRole;
                setRole(next);
                // A window typed for an Auditor must not travel with someone else.
                if (next !== "auditor") {
                  setScopeFrom("");
                  setScopeTo("");
                  setFilings([]);
                }
                setRefusal(null);
                checks.clear();
              }}
            >
              {ROLES.map((r) => (
                <option key={r.value} value={r.value}>
                  {r.label}
                </option>
              ))}
            </select>
          </label>
        </span>
        <span className={cls(own.hint)}>{CAN[role]}</span>

        {already ? (
          // **A control that predictably does nothing is not offered** (design rule 6): the submit
          // is disabled for as long as this shows, and the sentence names the control that works.
          <p className={cls(own.refused)} role="alert" data-testid="invite-already-member" data-status={already.status}>
            {already.status === "active"
              ? `${already.display_name} is already a ${(LABEL[already.role] ?? already.role).toLowerCase()} in this workspace. An invitation does not change an existing member's role — change it in the list of members.`
              : `${already.display_name} is already a member here with access ${already.status}. An invitation cannot restore it — use Restore workspace access in the list of members.`}
          </p>
        ) : null}

        {auditor ? (
          <span className={cls(own.fieldRow)} data-testid="invite-engagement">
            <label className={cls(own.field)}>
              Data from
              <input
                type="date"
                value={scopeFrom}
                required
                {...checks.field("from")}
                aria-invalid={refused === "period" || checks.errors.from !== undefined}
                data-testid="invite-period-from"
                onChange={(event) => {
                  checks.touch("from");
                  setScopeFrom(event.target.value);
                }}
              />
              <FieldError {...checks.say("from")} />
            </label>
            <label className={cls(own.field)}>
              Data to
              <input
                type="date"
                value={scopeTo}
                required
                {...checks.field("to")}
                aria-invalid={refused === "period" || checks.errors.to !== undefined}
                data-testid="invite-period-to"
                onChange={(event) => {
                  checks.touch("to");
                  setScopeTo(event.target.value);
                }}
              />
              <FieldError {...checks.say("to")} />
            </label>
          </span>
        ) : null}

        {auditor ? (
          <fieldset
            className={cls(own.filings)}
            aria-invalid={refused === "filings"}
            data-testid="invite-filings"
          >
            <legend>Filings they are engaged for</legend>
            <Loaded
              query={obligations}
              of="This workspace's filings"
              empty="No filing has been determined yet, so an Auditor cannot be invited: an engagement is for a filing."
            >
              {(rows) => (
                <ul>
                  {rows.map((row, index) => (
                    <li key={row.obligation_id}>
                      <label>
                        <input
                          type="checkbox"
                          checked={filings.includes(row.obligation_id)}
                          data-field={index === 0 ? "filings" : undefined}
                          aria-invalid={index === 0 && checks.errors.filings !== undefined ? true : undefined}
                          data-testid="invite-filing"
                          onChange={(event) => {
                            checks.touch("filings");
                            setFilings((current) =>
                              event.target.checked
                                ? [...current, row.obligation_id]
                                : current.filter((id) => id !== row.obligation_id),
                            );
                          }}
                        />{" "}
                        {filingLabel({
                          legal_entity_name: row.legal_entity_name,
                          regime_name: row.regime_name,
                          financial_year_end: row.financial_year_end,
                        })}
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </Loaded>
            <FieldError {...checks.say("filings")} />
            <span className={cls(own.hint)}>
              The companies they can read follow from these: a parent&rsquo;s consolidated filing
              reaches the subsidiaries inside it, and a subsidiary&rsquo;s filing reaches nothing
              above it. Choose none and they cannot be invited.
            </span>
          </fieldset>
        ) : null}

        <label className={cls(own.field)}>
          {auditor ? "Access ends" : "Access ends (optional)"}
          <input
            type="date"
            value={validTo}
            required={auditor}
            {...checks.field("end")}
            aria-invalid={refused === "end" || checks.errors.end !== undefined}
            data-testid="invite-valid-to"
            onChange={(event) => {
              checks.touch("end");
              setValidTo(event.target.value);
            }}
          />
          <FieldError {...checks.say("end")} />
          <span className={cls(own.hint)}>
            {auditor
              ? "The last day they may sign in. It can be after the period ends — an assurer reads a year during the next one. An engagement is for a time, not for ever."
              : "Leave empty for a colleague. Set it for a contractor whose access should stop."}
          </span>
        </label>
        {auditor ? (
          <span className={cls(own.hint)}>
            An auditor limited to a period cannot read the ledger&rsquo;s hash chain, because a
            chain filtered by a permission cannot be verified.
          </span>
        ) : null}

        {refused ? (
          <p className={cls(own.refused)} role="alert" data-testid="invite-refused" data-field={refused}>
            {refusal instanceof Error ? refusal.message : "That invitation was refused."}
          </p>
        ) : refusal instanceof Refused && refusal.code === ALREADY_A_MEMBER ? (
          /* The server's own sentence and remedy for an address that belongs to a member (active or
             suspended). The roster check above usually stops this first; it fires when the roster
             had not loaded or was stale. Nothing was issued, so there is no retry. */
          <p className={cls(own.refused)} role="alert" data-testid="invite-already-member-refused">
            {refusal.message}
          </p>
        ) : (
          <WriteFailed error={refusal} of="that invitation" />
        )}

        <span>
          <button type="submit" className={styles.primary} disabled={invite.isPending} data-testid="invite-submit">
            {invite.isPending ? "Issuing…" : "Issue invitation"}
          </button>
        </span>
      </form>
    </Panel>
  );
}

/** Invitations that have not been accepted: withdraw one, or replace it with a new link.
 *
 * Here because issuing a second invitation to an address that already has one is refused, and the
 * refusal says to revoke the first — a remedy with no control behind it would be a dead end. The
 * same is true of an invitation that has **expired**: it is still the one outstanding invitation to
 * that address, so the only way forward is to replace it, which is what *Re-send* does.
 *
 * **A re-send replaces; it does not add.** The server revokes the earlier link in the same
 * transaction that creates the new one, so the screen never shows two live links to one address and
 * says so beside the control. The Admin types the address again because the server keeps only a
 * fingerprint of it (rule 4) and cannot fill it in; a typo changes nothing, and the screen says that
 * too, using the server's own sentence for the refusal.
 *
 * **The new token is a bearer credential**, so it is held in this component's state only — not the
 * query cache (`gcTime: 0` in the hook), not the URL, not storage — and dropped when the Admin says
 * they have passed it on, exactly as an issued invitation's is. */
export function WaitingPanel() {
  const waiting = useInvitations(true);
  const revoke = useRevokeInvitation();
  const resend = useResendInvitation();
  const [refusal, setRefusal] = useState<unknown>(null);
  //: Which invitation's re-send form is open, and the address typed into it. One at a time: two
  //: open forms is two addresses a person can put against the wrong invitation.
  const [resending, setResending] = useState<string | null>(null);
  const [address, setAddress] = useState("");
  const [resendRefusal, setResendRefusal] = useState<unknown>(null);
  const [replacement, setReplacement] = useState<{
    token: string;
    expiresAt: string;
    emailRequested: boolean;
  } | null>(null);

  async function withdraw(claimId: string) {
    setRefusal(null);
    try {
      await revoke.mutateAsync(claimId);
    } catch (failure) {
      setRefusal(failure);
    }
  }

  async function replace(claimId: string) {
    setResendRefusal(null);
    try {
      const result = await resend.mutateAsync({ claimId, email: address.trim() });
      setReplacement({
        token: result.token,
        expiresAt: result.expires_at,
        emailRequested: result.email_requested,
      });
      setResending(null);
      setAddress("");
    } catch (failure) {
      setResendRefusal(failure);
    }
  }

  const mismatched = resendRefusal instanceof Refused && resendRefusal.code === RESEND_ADDRESS_MISMATCH;

  return (
    <Panel title="Waiting to be accepted" column="9 / 13" row="1">
      {replacement ? (
        <div className={cls(own.issued)} data-testid="invitation-resent">
          <strong>New invitation</strong>
          <code className={cls(own.token)} data-testid="invitation-resent-token">
            {replacement.token}
          </code>
          <span>
            The earlier link no longer works. Shown once; it cannot be fetched again. It works only
            with the address you typed, and expires on {shortDate(replacement.expiresAt)}.{" "}
            {replacement.emailRequested
              ? "An email has been requested, but delivery is not confirmed — if it does not arrive, give them this yourself."
              : "No email was requested — give them this yourself."}
          </span>
          <button
            type="button"
            className={styles.secondary}
            data-testid="invitation-resent-done"
            onClick={() => setReplacement(null)}
          >
            I have passed it on
          </button>
        </div>
      ) : null}
      <Loaded query={waiting} of="Outstanding invitations" empty="No invitations are waiting.">
        {(rows) => (
          <ul className={cls(own.waiting)} data-testid="invitations-waiting">
            {rows.map((row) => {
              const engagement = engagementLine(row);
              const expired = new Date(row.expires_at).getTime() <= Date.now();
              const open = resending === row.claim_id;
              return (
                <li
                  key={row.claim_id}
                  data-testid="invitation-waiting"
                  data-claim-id={row.claim_id}
                  data-role={row.role}
                >
                  <span>
                    <strong>{LABEL[row.role] ?? row.role}</strong> ·{" "}
                    <span data-testid="invitation-expiry" data-expired={expired}>
                      {expired ? "expired" : "expires"} {shortDate(row.expires_at)}
                    </span>
                    {engagement ? <span className={cls(own.can)}>{engagement}</span> : null}
                  </span>
                  <span className={cls(own.rowActions)}>
                    <button
                      type="button"
                      className={styles.fixLink}
                      disabled={resend.isPending}
                      data-testid="invitation-resend"
                      aria-expanded={open}
                      onClick={() => {
                        setResendRefusal(null);
                        setAddress("");
                        setResending(open ? null : row.claim_id);
                      }}
                    >
                      Re-send
                    </button>
                    <button
                      type="button"
                      className={styles.fixLink}
                      disabled={revoke.isPending}
                      data-testid="invitation-revoke"
                      onClick={() => void withdraw(row.claim_id)}
                    >
                      Withdraw
                    </button>
                  </span>
                  {open ? (
                    <form
                      className={cls(own.resendForm)}
                      data-testid="invitation-resend-form"
                      onSubmit={(event) => {
                        event.preventDefault();
                        void replace(row.claim_id);
                      }}
                    >
                      <label className={cls(own.field)}>
                        Type the address this was issued to
                        <input
                          type="email"
                          value={address}
                          required
                          autoComplete="off"
                          aria-invalid={mismatched}
                          data-testid="invitation-resend-email"
                          onChange={(event) => setAddress(event.target.value)}
                        />
                      </label>
                      <span className={cls(own.hint)}>
                        EmitIQ keeps only a fingerprint of the address, so it cannot fill this in.
                        The earlier link stops working when this succeeds; nothing changes if the
                        address is wrong.
                      </span>
                      {mismatched ? (
                        <p
                          className={cls(own.refused)}
                          role="alert"
                          data-testid="invitation-resend-refused"
                          data-code={RESEND_ADDRESS_MISMATCH}
                        >
                          {resendRefusal.message}
                        </p>
                      ) : (
                        <WriteFailed
                          error={resendRefusal}
                          of="that re-send"
                          onReload={() => {
                            void waiting.refetch();
                            setResendRefusal(null);
                          }}
                        />
                      )}
                      <span className={cls(own.rowActions)}>
                        <button
                          type="submit"
                          className={styles.primary}
                          disabled={!address.includes("@") || resend.isPending}
                          data-testid="invitation-resend-submit"
                        >
                          {resend.isPending ? "Replacing…" : "Replace and re-send"}
                        </button>
                        <button
                          type="button"
                          className={styles.secondary}
                          data-testid="invitation-resend-cancel"
                          onClick={() => {
                            setResending(null);
                            setAddress("");
                            setResendRefusal(null);
                          }}
                        >
                          Cancel
                        </button>
                      </span>
                    </form>
                  ) : null}
                </li>
              );
            })}
          </ul>
        )}
      </Loaded>
      <WriteFailed
        error={refusal}
        of="that withdrawal"
        onReload={() => {
          void waiting.refetch();
          setRefusal(null);
        }}
      />
    </Panel>
  );
}

/** What each role is offered, as a matrix - the reference an Admin reads before granting one.
 *
 * **Every cell is read from a rule the product already enforces, not typed here.** The places a role is
 * offered are `routeTable.ts`'s own `roles` (the same list the rail and the palette read); the acts are
 * the client's mirrors of the server's role sets (`mayEnterData`, `maySubmit`, `mayHaveQueue`, and the
 * Admin's alone). The server's 403 is the authority and these mirrors exist so a screen does not offer
 * what would be refused. A role that is not offered a place sees no item for it, and a person who types
 * the address is told whose decision it is.
 */
function RoleMatrix() {
  const places = NAV_SECTIONS.filter((section) => section.rail !== false);
  const acts: Array<{ id: string; label: string; may: (role: string) => boolean }> = [
    { id: "enter", label: "Add data: upload, enter, import", may: (role) => mayEnterData(role) },
    { id: "submit", label: "Send a record for approval", may: (role) => maySubmit(role) },
    { id: "decide", label: "Decide an approval", may: (role) => mayHaveQueue(role) },
    { id: "admin", label: "Administer people and settings", may: (role) => role === "admin" },
  ];
  const rows: Array<{ id: string; label: string; may: (role: string) => boolean }> = [
    ...acts,
    ...places.map((section) => ({
      id: `place-${section.id}`,
      label: `Offered ${section.label}`,
      may: (role: string) => section.roles.includes(role as (typeof section.roles)[number]),
    })),
  ];
  const columns: GridColumn<(typeof rows)[number]>[] = [
    { id: "what", header: "What", cell: (row) => row.label },
    ...ROLES.map((role) => ({
      id: role.value,
      header: role.label,
      align: "center" as const,
      cell: (row: (typeof rows)[number]) =>
        row.may(role.value) ? (
          <span aria-label="Yes">Yes</span>
        ) : (
          <span aria-label="No" className={own.can}>
            -
          </span>
        ),
    })),
  ];
  return (
    <GridTable
      caption="What each role is offered"
      columns={columns}
      rows={rows}
      rowKey={(row) => row.id}
      rowNumbers={false}
      testId="role-matrix"
    />
  );
}

/** Who is in the workspace, as a grid: the Admin's People and roles (ADR-0212 decision 1, KD
 * 2026-11-01). A table page of its own - tiles, one filter line, one grid - with the two things that
 * used to be tabs of People behind header buttons: **Invite** (the form and the invitations waiting,
 * in one drawer) and **Role matrix** (what each role is offered).
 *
 * The screen is the Admin's alone; a direct visit by anyone else is told whose decision it is and
 * asks for nothing (`people-admin-only`). The rest of what this file's header says holds: a role is a
 * capability and says so, the last Admin's controls are explained before they are hit, an Auditor is
 * made by an invitation and never by a role change, and an invitation cannot change a member.
 */
export function PeopleBody() {
  const roster = useMembers(true);
  const members = roster.data ?? [];
  const waiting = useInvitations(true);
  const update = useUpdateMember();
  const [refusal, setRefusal] = useState<unknown>(null);
  const [filter, setFilter] = useState<FilterState>(EMPTY_FILTERS);
  const [drawer, setDrawer] = useState<"invite" | "roles" | null>(null);
  //: The role the invite form opens on. "Fix engagement" opens it on Auditor, which is the server's
  //: remedy for an auditor with no filing named: invite a new auditor for the filing. Bumped each time
  //: so the form mounts fresh and a half-typed invitation is not carried between two reasons.
  const [inviteFor, setInviteFor] = useState<{ role: MemberRole; seed: number }>({ role: "viewer", seed: 0 });
  //: A role change waits for the Admin to confirm it, with what the person gains and loses said.
  const [pending, setPending] = useState<{ member: Member; role: MemberRole } | null>(null);

  const activeAdmins = members.filter((m) => m.role === "admin" && m.status === "active").length;

  async function change(member: Member, patch: { role?: MemberRole; status?: "active" | "suspended" }) {
    setRefusal(null);
    try {
      await update.mutateAsync({ userId: member.user_id, ...patch });
    } catch (error) {
      setRefusal(error);
    }
  }

  // **Counted from the roster that was read, and only if it was read.** A failed roster shows a dash
  // and says so; a tile of `0` over a failed read is the defect `data ?? []` was.
  const read = roster.data !== undefined;
  const active = members.filter((m) => m.status === "active").length;
  const auditors = members.filter((m) => m.role === "auditor");

  const picked = filter.values.role ?? [];
  const status = filter.values.status ?? [];
  const needle = filter.search.trim().toLowerCase();
  const rows = members.filter((m) => {
    if (picked.length > 0 && !picked.includes(m.role)) return false;
    if (status.length > 0 && !status.includes(m.status)) return false;
    return !needle || `${m.display_name} ${m.email}`.toLowerCase().includes(needle);
  });
  const { sorted, sort, setSort } = useGridSort(rows, {
    person: (m) => m.display_name,
    role: (m) => LABEL[m.role] ?? m.role,
  });

  const columns: GridColumn<Member>[] = [
    {
      id: "person",
      header: "Person",
      sortable: true,
      cell: (member) => (
        <span className={cls(own.who)} data-testid="member-row" data-user-id={member.user_id}>
          <span className={cls(own.name)}>
            {/* The way to this person's own page: their access, their work and what they have done. */}
            <Link
              className={cls(own.nameLink)}
              to="/people/$userId"
              params={{ userId: member.user_id }}
              search={keepSearch}
              data-testid="member-open"
            >
              {member.display_name}
            </Link>
            {member.is_you ? <span className={cls(own.you)}>you</span> : null}
            {member.status !== "active" ? <span className={cls(own.suspended)}>{member.status}</span> : null}
          </span>
          <span className={cls(own.email)}>{member.email}</span>
        </span>
      ),
    },
    {
      id: "role",
      header: "Role",
      sortable: true,
      cell: (member) => {
        // **The refusal, explained before it is hit.** Offering a change that predictably 409s tells
        // somebody they have a permissions problem they cannot fix.
        const lastAdmin = member.role === "admin" && member.status === "active" && activeAdmins <= 1;
        return (
          <select
            aria-label={`Role for ${member.display_name}`}
            value={member.role}
            disabled={lastAdmin || member.status === "erased" || update.isPending}
            onChange={(event) => setPending({ member, role: event.target.value as MemberRole })}
          >
            {/* An Auditor is made by an invitation, never by a role change: the server refuses it, so
                the option is not offered to anybody who is not one. */}
            {ROLES.filter((role) => role.value !== "auditor" || member.role === "auditor").map((role) => (
              <option key={role.value} value={role.value}>
                {role.label}
              </option>
            ))}
          </select>
        );
      },
    },
    {
      id: "engagement",
      header: "Engagement",
      cell: (member) => {
        if (member.role !== "auditor") {
          return <span className={cls(own.can)}>-</span>;
        }
        const period = periodOf(member.period_scope);
        return (
          <span className={cls(own.who)}>
            {/* An auditor with no period scope reads every year, which an engagement does not
                entitle them to. A finding, not a default; the sentence is behind the info control. */}
            {period ? (
              <span className={cls(own.can)} data-testid="auditor-engagement">
                Data from {shortDate(period.from ?? null)} to {shortDate(period.to ?? null)}
              </span>
            ) : (
              <span className={cls(own.chipRow)}>
                <StatusChip tone="warn" size="sm" testId="auditor-unscoped">
                  No period
                </StatusChip>
                <InfoPopover infoKey="admin.people-engagement" />
              </span>
            )}
            {/* **Whose** data. `whole_workspace` is an auditor with no filing named - a membership
                made before an engagement named anything - and it is a finding, not a default. */}
            {member.entity_scope === "whole_workspace" ? (
              <span className={cls(own.chipRow)}>
                <StatusChip tone="warn" size="sm" testId="auditor-whole-workspace">
                  Reads every company
                </StatusChip>
                <InfoPopover infoKey="admin.people-engagement" />
                <button
                  type="button"
                  className={styles.fixLink}
                  data-testid="auditor-fix-engagement"
                  onClick={() => {
                    setInviteFor((now) => ({ role: "auditor", seed: now.seed + 1 }));
                    setDrawer("invite");
                  }}
                >
                  Fix engagement
                </button>
              </span>
            ) : null}
            {member.entity_scope === "named" ? (
              <ul className={cls(own.can)} data-testid="auditor-filings">
                {(member.engagement ?? []).map((filing) => (
                  <li key={filing.obligation_id}>Engaged for {filingLabel(filing)}</li>
                ))}
              </ul>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "access",
      header: "Access",
      cell: (member) => {
        const lastAdmin = member.role === "admin" && member.status === "active" && activeAdmins <= 1;
        return (
          <span className={cls(own.who)}>
            {/* What those filings reach **by site**, from the database's own policies (ADR-0093). */}
            <EngagementReach member={member} />
            {member.valid_to ? (
              <span className={cls(own.can)} data-testid="member-ends">
                Access ends {shortDate(member.valid_to)}
              </span>
            ) : null}
            <EngagementPeriod member={member} lastAdmin={lastAdmin} onFailed={setRefusal} />
            {lastAdmin ? (
              <span className={cls(own.why)}>
                The only active Admin. Removing this would leave nobody able to add one - every route
                to administration requires an Admin. Promote a colleague first.
              </span>
            ) : null}
          </span>
        );
      },
    },
    {
      id: "act",
      header: "Workspace access",
      cell: (member) => {
        const lastAdmin = member.role === "admin" && member.status === "active" && activeAdmins <= 1;
        return (
          <button
            type="button"
            className={styles.fixLink}
            disabled={lastAdmin || member.status === "erased" || update.isPending}
            onClick={() =>
              void change(member, { status: member.status === "active" ? "suspended" : "active" })
            }
          >
            {member.status === "active" ? "Suspend workspace access" : "Restore workspace access"}
          </button>
        );
      },
    },
  ];

  return (
    <>
      <TablePage
        icon={UsersThree}
        title="Admin"
        label="People and roles"
        actions={
          <span className={cls(own.headActions)}>
            <button type="button" onClick={() => setDrawer("roles")} data-testid="roles-open">
              Role matrix
            </button>
            <button
              type="button"
              className={styles.primary}
              onClick={() => {
                setInviteFor((now) => ({ role: "viewer", seed: now.seed + 1 }));
                setDrawer("invite");
              }}
              data-testid="invite-open"
            >
              Invite
            </button>
          </span>
        }
        tiles={
          <TileStrip label="People at a glance">
            <Tile
              value={read ? String(active) : "-"}
              label="Active members"
              infoKey="admin.people"
              read={roster}
              testId="people-active"
            />
            <Tile value={read ? String(activeAdmins) : "-"} label="Active Admins" read={roster} testId="people-admins" />
            <Tile value={read ? String(auditors.length) : "-"} label="Auditors" read={roster} testId="people-auditors" />
            <Tile
              value={waiting.data ? String(waiting.data.length) : "-"}
              label="Invitations waiting"
              read={waiting}
              testId="people-waiting"
              onOpen={waiting.data ? () => setDrawer("invite") : undefined}
              openLabel="Invitations waiting. Open the invitations"
            />
          </TileStrip>
        }
        filters={
          <FilterLine
            filters={[
              {
                id: "role",
                label: "Role",
                options: ROLES.map((role) => ({ value: role.value, label: role.label })),
              },
              {
                id: "status",
                label: "Status",
                options: [
                  { value: "active", label: "Active" },
                  { value: "suspended", label: "Suspended" },
                ],
              },
            ]}
            state={filter}
            onChange={setFilter}
            resultCount={read ? { shown: rows.length, total: members.length, noun: "people" } : undefined}
          />
        }
        table={
          roster.error ? undefined : (
            <>
              {waiting.error ? (
                <ReadFailed
                  of="The count of invitations waiting"
                  error={waiting.error}
                  onRetry={() => void waiting.refetch()}
                />
              ) : null}
              <WriteFailed
                error={refusal}
                of="this workspace's people"
                onReload={() => {
                  void roster.refetch();
                  setRefusal(null);
                }}
              />
              <GridTable
                caption="The people in this workspace"
                columns={columns}
                rows={sorted}
                rowKey={(member) => member.user_id}
                rowLabel={(member) => member.display_name}
                loading={roster.isPending}
                sort={sort}
                onSortChange={setSort}
                empty={<EmptyState title="Nobody else is in this workspace yet." compact testId="members-empty" />}
                testId="members"
              />
            </>
          )
        }
        state={
          roster.error ? (
            <ReadFailed
              of="The people in this workspace"
              error={roster.error}
              onRetry={() => void roster.refetch()}
            />
          ) : undefined
        }
      />
      <Drawer
        open={pending !== null}
        onClose={() => setPending(null)}
        title="Change role"
        width="sm"
        testId="role-change-drawer"
        footer={
          pending ? (
            <span className={cls(own.headActions)}>
              <button type="button" onClick={() => setPending(null)} data-testid="role-change-cancel">
                Cancel
              </button>
              <button
                type="button"
                className={styles.primary}
                data-testid="role-change-confirm"
                disabled={update.isPending}
                onClick={() => {
                  const { member, role } = pending;
                  setPending(null);
                  void change(member, { role });
                }}
              >
                Change role
              </button>
            </span>
          ) : null
        }
      >
        {pending ? (
          <div className={cls(own.drawerStack)} data-testid="role-change-summary">
            <p className={styles.note}>
              <strong>{pending.member.display_name}</strong> goes from{" "}
              <strong>{LABEL[pending.member.role] ?? pending.member.role}</strong> to{" "}
              <strong>{LABEL[pending.role] ?? pending.role}</strong>.
            </p>
            <ul className={cls(own.roles)}>
              <li>
                <strong>Now</strong>
                <span className={cls(own.can)}>{CAN[pending.member.role] ?? pending.member.role}</span>
              </li>
              <li>
                <strong>After</strong>
                <span className={cls(own.can)} data-testid="role-change-after">
                  {CAN[pending.role] ?? pending.role}
                </span>
              </li>
            </ul>
          </div>
        ) : null}
      </Drawer>
      <Drawer
        open={drawer === "invite"}
        onClose={() => setDrawer(null)}
        title="Invite someone"
        width="lg"
      >
        <div className={cls(own.drawerStack)}>
          <InvitePanel key={inviteFor.seed} initialRole={inviteFor.role} />
          <WaitingPanel />
          <p className={styles.note} data-testid="invitation-delivery-note">
            Issuing or re-sending an invitation shows you the token and asks for an email to that
            address where email is set up. Delivery is not confirmed, and the token also works entered
            at the join page, so check that the person received it or hand it over yourself.
          </p>
        </div>
      </Drawer>
      <Drawer open={drawer === "roles"} onClose={() => setDrawer(null)} title="Role matrix" width="lg">
        <div className={cls(own.drawerStack)}>
          <ul className={cls(own.roles)}>
            {ROLES.map((role) => (
              <li key={role.value}>
                <strong>{role.label}</strong>
                <span className={cls(own.can)}>{role.can}</span>
              </li>
            ))}
          </ul>
          <RoleMatrix />
        </div>
      </Drawer>
    </>
  );
}

function PeopleScreenBody() {
  const screen = useScreen();
  const { data: me } = useMe();
  const isAdmin = me?.role === "admin";

  if (!isAdmin) {
    return (
      <ScreenFrame screen={screen} title="People" crumb="who is in this workspace">
        <Panel title="People" column="1 / 8">
          <p className={styles.note} data-testid="people-admin-only">
            {/* Told whose decision it is, rather than shown a list that 403s. Every other role can
                already tell colleagues exist - a task has an owner, an approval has a reviewer - and
                none of them needs the list of who could be made an Approver. */}
            An <strong>Admin</strong> administers people. Who may approve a filing is the most
            consequential setting in this workspace, and it is not a shared one.
          </p>
        </Panel>
      </ScreenFrame>
    );
  }
  return (
    <ScreenFrame screen={screen} title="People">
      <PeopleBody />
    </ScreenFrame>
  );
}

export function PeopleScreen() {
  return (
    <RequireSession>
      <PeopleScreenBody />
    </RequireSession>
  );
}
